import {
    ArrowRight,
    CalendarClock,
    ChevronLeft,
    Clock3,
    IndianRupee,
    LoaderCircle,
    PencilLine,
    Settings2,
    ShieldCheck,
    TriangleAlert,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CycleConfigViewScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const fmtAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const fmtDateTime = (value) => {
    if (!value) return '—'
    const date = new Date(value.replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getRunStatusMeta = (status) => {
    const normalized = String(status || '').toUpperCase()
    if (normalized === 'FINALIZED') {
        return { label: 'Finalized', className: styles.statusFinalized }
    }
    if (normalized === 'REVIEW') {
        return { label: 'Review', className: styles.statusReview }
    }
    return { label: 'Draft', className: styles.statusDraft }
}

const CycleConfigViewScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [configData, setConfigData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    const fetchCycleConfig = useCallback(async ({ silent = false } = {}) => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setErrorMessage('Please select a store first to view payroll settings.')
            setIsLoading(false)
            return
        }

        if (silent) {
            setIsRefreshing(true)
        } else {
            setIsLoading(true)
        }
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'getStoreSalaryCycleConfig', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load cycle configuration.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                if (!respo.response?.isConfigured) {
                    navigate('/payroll/setup/cycle-config', { replace: true })
                    return
                }
                setConfigData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setErrorMessage(respo?.message || 'Failed to load configuration.')
            toast.error(respo?.message || 'Failed to load configuration.')
        } catch (error) {
            console.error('fetchCycleConfig error:', error)
            setErrorMessage('Network error. Please check your connection.')
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, navigate])

    useEffect(() => {
        fetchCycleConfig()
    }, [fetchCycleConfig])

    useEffect(() => {
        if (location.state?.refreshCycleConfig) {
            fetchCycleConfig({ silent: true })
        }
    }, [fetchCycleConfig, location.state])

    const handleEditPress = () => {
        if (!configData?.config) return
        navigate('/payroll/setup/cycle-config/edit', {
            state: {
                currentConfig: configData.config,
            },
        })
    }

    const handleViewRun = () => {
        if (!configData?.latestRun) return
        navigate('/payroll/run', {
            state: {
                latestRun: configData.latestRun,
                runIdentifier: configData.latestRun.runIdentifier,
            },
        })
    }

    const summary = configData?.config?.summary || {}
    const latestRun = configData?.latestRun || null
    const runStatusMeta = getRunStatusMeta(latestRun?.runStatus)
    const lateDeductionEnabled = Number(configData?.config?.lateDeductionEnabled || 0) === 1

    return (
        <div className={styles.page}>
            <div className={styles.headerRow}>
                <div className={styles.header}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <span className={styles.heroPill}>
                            <CalendarClock size={12} />
                            Payroll Setup
                        </span>
                        <h1 className={styles.headerTitle}>Payroll Settings</h1>
                        <p className={styles.headerSub}>Review the current payroll cycle rules and latest payroll impact.</p>
                    </div>
                </div>

                <button className={styles.editBtn} onClick={handleEditPress} disabled={!configData?.config || isLoading}>
                    <PencilLine size={15} />
                    Edit
                </button>
            </div>

            {isLoading ? (
                <div className={styles.loadingCard}>
                    <LoaderCircle size={18} className={styles.spin} />
                    <span>Loading cycle configuration…</span>
                </div>
            ) : errorMessage ? (
                <div className={styles.errorCard}>
                    <TriangleAlert size={16} />
                    <div>
                        <strong>Unable to load payroll settings.</strong>
                        <p>{errorMessage}</p>
                    </div>
                </div>
            ) : (
                <div className={styles.contentWrap}>
                    {isRefreshing && (
                        <div className={styles.refreshingNote}>
                            <LoaderCircle size={14} className={styles.spin} />
                            Refreshing configuration…
                        </div>
                    )}

                    {latestRun && (
                        <div className={styles.runCard}>
                            <div className={styles.runHeader}>
                                <div>
                                    <span className={styles.cardEyebrow}>Latest Run</span>
                                    <h2 className={styles.runTitle}>{latestRun.monthLabel} payroll</h2>
                                </div>
                                <span className={`${styles.statusBadge} ${runStatusMeta.className}`}>
                                    {runStatusMeta.label}
                                </span>
                            </div>

                            <p className={styles.runMeta}>
                                {latestRun.totalStaffCount} staff · {fmtAmount(latestRun.totalNetPayout)} net payout
                            </p>
                            <p className={styles.runSub}>
                                Cycle {latestRun.cycleStartDate} → {latestRun.cycleEndDate}
                            </p>

                            <button className={styles.linkBtn} onClick={handleViewRun}>
                                View Run
                                <ArrowRight size={15} />
                            </button>
                        </div>
                    )}

                    <div className={styles.cardsGrid}>
                        <div className={styles.infoCard}>
                            <div className={styles.cardHead}>
                                <CalendarClock size={15} />
                                Cycle & Pay Date
                            </div>
                            <p className={styles.cardLine}>{summary.cycleLabel || 'Cycle configuration active'}</p>
                            <p className={styles.cardLine}>{summary.cycleStartLabel || 'Cycle start configured'}</p>
                            <p className={styles.cardLine}>{summary.payDayLabel || 'Pay day configured'}</p>
                        </div>

                        <div className={styles.infoCard}>
                            <div className={styles.cardHead}>
                                <Settings2 size={15} />
                                LWP & Attendance
                            </div>
                            <p className={styles.cardLine}>{summary.lwpMethodLabel || 'LWP method configured'}</p>
                            <p className={styles.cardLine}>{summary.halfDayLabel || 'Half day rules configured'}</p>
                            <p className={styles.cardLine}>{summary.gracePeriodLabel || 'Grace period configured'}</p>
                        </div>

                        <div className={styles.infoCard}>
                            <div className={styles.cardHeadWithBadge}>
                                <div className={styles.cardHead}>
                                    <Clock3 size={15} />
                                    Late Deduction
                                </div>
                                <span className={`${styles.smallBadge} ${lateDeductionEnabled ? styles.badgeActive : styles.badgeMuted}`}>
                                    {lateDeductionEnabled ? 'Active' : 'Disabled'}
                                </span>
                            </div>
                            <p className={styles.cardLine}>{summary.lateDeductionSummary || 'Late deduction rules configured'}</p>
                        </div>

                        <div className={styles.infoCard}>
                            <div className={styles.cardHead}>
                                <ShieldCheck size={15} />
                                Finalization
                            </div>
                            <p className={styles.cardLine}>{summary.autoLockLabel || 'Finalization rule configured'}</p>
                        </div>
                    </div>

                    <div className={styles.metaCard}>
                        <div className={styles.metaRow}>
                            <span className={styles.metaLabel}>Created</span>
                            <span className={styles.metaValue}>{fmtDateTime(configData?.config?.createdOn)}</span>
                        </div>
                        <div className={styles.metaRow}>
                            <span className={styles.metaLabel}>Last updated</span>
                            <span className={styles.metaValue}>{fmtDateTime(configData?.config?.updatedOn)}</span>
                        </div>
                        {latestRun && (
                            <div className={styles.metaRow}>
                                <span className={styles.metaLabel}>Latest payout</span>
                                <span className={styles.metaValue}>
                                    <IndianRupee size={13} />
                                    {Number(latestRun.totalNetPayout || 0).toLocaleString('en-IN')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default CycleConfigViewScreen